import { useState, useEffect, useRef } from "react";
import {
  ThumbsUp,
  ThumbsDown,
  Bookmark,
  ImageOff,
  Eye,
  EyeOff,
  AlertTriangle,
} from "lucide-react";
import LoadingSpinner from "./LoadingSpinner";
import { toggleThumb } from "../../api/feed";
import ShareArticleButton from "./ShareArticleButton";

const APP_BLUE = "#1c9dea";

const getPreviewImage = (value) => {
  if (typeof value !== "string") return "";
  return value.trim();
};

const ActionPill = ({ active, label, onClick, children }) => {
  const handle = (e) => {
    e.stopPropagation();
    onClick?.(e);
  };

  return (
    <button
      type="button"
      aria-pressed={!!active}
      aria-label={label}
      onClick={handle}
      className={[
        "inline-flex items-center justify-center rounded-full h-9 w-9 border backdrop-blur-sm transition-all duration-200",
        "active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#1c9dea]/40",
        active
          ? "border-[#1c9dea]/50 bg-gray-900/90"
          : "border-[#0EA5E9]/25 bg-gray-900/80 hover:scale-105 hover:border-[#38BDF8]/40",
      ].join(" ")}
    >
      {children}
    </button>
  );
};

const NSFWOverlay = ({ revealed, onReveal, onHide }) => {
  if (revealed) {
    return (
      <button
        type="button"
        onClick={onHide}
        className="absolute right-2.5 top-2.5 z-30 inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-black/70 px-2.5 py-1 text-[11px] font-medium text-white/85 backdrop-blur transition hover:bg-black/85"
      >
        <EyeOff size={12} />
        Hide
      </button>
    );
  }

  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/35 backdrop-blur-[1px]">
      <div className="mx-4 flex max-w-[250px] flex-col items-center rounded-2xl border border-white/15 bg-black/75 px-4 py-3 text-center shadow-2xl">
        <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-full border border-red-400/30 bg-red-500/15 text-red-200">
          <AlertTriangle size={17} />
        </div>

        <div className="text-sm font-semibold text-white">NSFW content</div>

        <div className="mt-1 text-xs leading-relaxed text-white/65">
          This preview is hidden because it was marked NSFW.
        </div>

        <button
          type="button"
          onClick={onReveal}
          className="mt-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-white/20"
        >
          <Eye size={14} />
          View anyway
        </button>
      </div>
    </div>
  );
};

const ArticleCard = (props) => {
  const [isHovered, setIsHovered] = useState(false);
  const [localLike, setLocalLike] = useState(props.articleLike);
  const [localDislike, setLocalDislike] = useState(props.articleDislike);
  const [localCatalog, setLocalCatalog] = useState(props.articleCatalog);
  const [isBroken, setIsBroken] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isNSFWRevealed, setIsNSFWRevealed] = useState(false);

  useEffect(() => {
    setLocalLike(props.articleLike);
  }, [props.articleLike]);

  useEffect(() => {
    setLocalDislike(props.articleDislike);
  }, [props.articleDislike]);

  useEffect(() => {
    setLocalCatalog(props.articleCatalog);
  }, [props.articleCatalog]);

  // Same preview resolution as the home timeline: video screenshot first,
  // then the page screenshot.
  const videoScreenshot = getPreviewImage(props.article?.video_screenshot);
  const fallbackScreenshot = getPreviewImage(props.article?.screenshot);
  const [activeImage, setActiveImage] = useState(
    videoScreenshot || fallbackScreenshot,
  );
  const imgRef = useRef(null);

  useEffect(() => {
    const nextImage = videoScreenshot || fallbackScreenshot;

    setIsNSFWRevealed(false);
    setIsImageLoaded(false);
    setActiveImage(nextImage);
    // No image to load means no load/error event will ever fire, so the card
    // would sit on the spinner forever. Go straight to the unavailable state.
    setIsBroken(!nextImage);
  }, [props.article?.document_id, videoScreenshot, fallbackScreenshot]);

  // A cached image can finish before React attaches onLoad (or the src is
  // unchanged after a reset), so pick up an already-complete image.
  useEffect(() => {
    const img = imgRef.current;
    if (!img || !activeImage) return;
    if (img.complete && img.naturalWidth > 0) setIsImageLoaded(true);
  }, [activeImage, props.article?.document_id]);

  const docId = props.article?.document_id;
  const isNSFW = props.article?.nsfw === true;
  const shouldBlurNSFW = isNSFW && !isNSFWRevealed;

  const handleLikeClick = () => {
    const next = !localLike;
    setLocalLike(next);
    toggleThumb({ document_id: docId, action: "like" });

    let newDislike = localDislike;

    if (localDislike) {
      setLocalDislike(false);
      toggleThumb({ document_id: docId, action: "dislike" });
      newDislike = false;
    }

    props.onInteract?.({
      like: next,
      dislike: newDislike,
      catalog: localCatalog,
    });
  };

  const handleDislikeClick = () => {
    const next = !localDislike;
    setLocalDislike(next);
    toggleThumb({ document_id: docId, action: "dislike" });

    let newLike = localLike;

    if (localLike) {
      setLocalLike(false);
      toggleThumb({ document_id: docId, action: "like" });
      newLike = false;
    }

    props.onInteract?.({
      like: newLike,
      dislike: next,
      catalog: localCatalog,
    });
  };

  const handleCatalogClick = () => {
    const next = !localCatalog;
    setLocalCatalog(next);
    toggleThumb({ document_id: docId, action: "catalog" });

    props.onInteract?.({
      like: localLike,
      dislike: localDislike,
      catalog: next,
    });
  };

  const imageClick = (article) => {
    props.investigateDoc(article.document_id);
  };

  const handleImageError = () => {
    // Video screenshot failed: try the page screenshot before giving up.
    if (activeImage === videoScreenshot && fallbackScreenshot) {
      setActiveImage(fallbackScreenshot);
      setIsImageLoaded(false);
      return;
    }

    setIsBroken(true);

    const hasContent =
      props.article?.title ||
      props.article?.content ||
      props.article?.target_type ||
      props.article?.source;

    if (!hasContent) props.onBroken?.();
  };

  const handleRevealNSFW = (e) => {
    e.stopPropagation();
    setIsNSFWRevealed(true);
  };

  const handleHideNSFW = (e) => {
    e.stopPropagation();
    setIsNSFWRevealed(false);
  };

  const showActions = isHovered || localLike || localDislike || localCatalog;

  const nsfwBadge = isNSFW && (
    <div className="absolute top-2.5 right-2.5 z-20 rounded-full border border-red-300/25 bg-red-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-100 backdrop-blur">
      NSFW
    </div>
  );

  const actionButtons = showActions && (
    <div className="absolute bottom-3 left-0 right-0 z-40 flex items-center justify-center gap-3 px-3">
      <ActionPill
        active={localLike}
        label={localLike ? "Unlike" : "Like"}
        onClick={handleLikeClick}
      >
        <ThumbsUp
          className="h-5 w-5"
          strokeWidth={1.5}
          style={{
            color: localLike ? APP_BLUE : "rgb(203 213 225)",
            fill: localLike ? APP_BLUE : "none",
            filter: localLike
              ? "drop-shadow(0 0 6px rgba(28,157,234,0.6))"
              : "none",
          }}
        />
      </ActionPill>

      <ActionPill
        active={localDislike}
        label={localDislike ? "Remove dislike" : "Dislike"}
        onClick={handleDislikeClick}
      >
        <ThumbsDown
          className="h-5 w-5"
          strokeWidth={1.5}
          style={{
            color: localDislike ? APP_BLUE : "rgb(203 213 225)",
            fill: localDislike ? APP_BLUE : "none",
            filter: localDislike
              ? "drop-shadow(0 0 6px rgba(28,157,234,0.6))"
              : "none",
          }}
        />
      </ActionPill>

      <ActionPill
        active={localCatalog}
        label={localCatalog ? "Remove bookmark" : "Bookmark"}
        onClick={handleCatalogClick}
      >
        <Bookmark
          className="h-5 w-5"
          strokeWidth={1.5}
          style={{
            color: localCatalog ? APP_BLUE : "rgb(203 213 225)",
            fill: localCatalog ? APP_BLUE : "none",
            filter: localCatalog
              ? "drop-shadow(0 0 6px rgba(28,157,234,0.6))"
              : "none",
          }}
        />
      </ActionPill>

      <ShareArticleButton
        documentId={docId}
        article={props.article}
        variant="pill"
      />
    </div>
  );

  if (
    isBroken &&
    !props.article?.title &&
    !props.article?.content &&
    !props.article?.target_type &&
    !props.article?.source
  ) {
    return null;
  }

  if (isBroken) {
    return (
      <div
        className={[
          "group relative h-96 w-full m-2 rounded-lg overflow-hidden cursor-pointer border backdrop-blur-sm transition-all duration-300",
          props.isActive
            ? "border-[#1c9dea] shadow-[0_0_12px_rgba(28,157,234,0.7),_0_0_30px_rgba(28,157,234,0.5),_0_0_60px_rgba(28,157,234,0.25)]"
            : "border-[#0EA5E9]/15 hover:border-[#0EA5E9]/30",
          props.isActive
            ? "bg-gray-900/50"
            : "bg-gray-900/40 hover:bg-gray-900/50",
        ].join(" ")}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => imageClick(props.article)}
      >
        <div className="relative h-full w-full flex flex-col">
          {props.hoverPreview && (
            <div className="relative z-20">{props.hoverPreview}</div>
          )}

          <div className="flex-1 flex flex-col items-center justify-center gap-2 px-4">
            <ImageOff size={32} style={{ color: "rgba(148,163,184,0.25)" }} />
            <span style={{ color: "rgba(148,163,184,0.3)", fontSize: 11 }}>
              Screenshot unavailable
            </span>
          </div>

          {!props.hoverPreview && props.typeBadge && (
            <div className="absolute top-2.5 left-2.5 z-10">
              {props.typeBadge}
            </div>
          )}

          {nsfwBadge}
        </div>

        {actionButtons}
      </div>
    );
  }

  return (
    <div
      className={[
        "group relative h-96 w-full m-2 rounded-lg overflow-hidden cursor-pointer border backdrop-blur-sm transition-all duration-300",
        props.isActive
          ? "border-[#1c9dea] shadow-[0_0_12px_rgba(28,157,234,0.7),_0_0_30px_rgba(28,157,234,0.5),_0_0_60px_rgba(28,157,234,0.25)]"
          : "border-[#0EA5E9]/15 hover:border-[#0EA5E9]/30",
        props.isActive
          ? "bg-gray-900/50"
          : "bg-gray-900/40 hover:bg-gray-900/50",
      ].join(" ")}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => imageClick(props.article)}
    >
      <div className="relative h-full w-full">
        {!isImageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <LoadingSpinner />
          </div>
        )}

        <img
          key={activeImage}
          ref={imgRef}
          className={[
            "absolute inset-0 h-full w-full object-cover object-top transition-all duration-300",
            isImageLoaded ? "opacity-100" : "opacity-0",
            shouldBlurNSFW ? "blur-xl scale-[1.03]" : "blur-0 scale-100",
          ].join(" ")}
          src={activeImage}
          alt={props.article?.title || "Article screenshot"}
          onLoad={() => setIsImageLoaded(true)}
          onError={handleImageError}
          loading="lazy"
          draggable={false}
        />

        <div className="absolute bottom-0 left-0 right-0 h-36 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />

        {props.typeBadge && (
          <div className="absolute top-2.5 left-2.5 z-10">
            {props.typeBadge}
          </div>
        )}

        {nsfwBadge}

        {isNSFW && (
          <NSFWOverlay
            revealed={isNSFWRevealed}
            onReveal={handleRevealNSFW}
            onHide={handleHideNSFW}
          />
        )}

        {props.hoverPreview && !shouldBlurNSFW && (
          <div className="absolute top-0 left-0 right-0 z-20">
            {props.hoverPreview}
          </div>
        )}
      </div>

      {actionButtons}
    </div>
  );
};

export default ArticleCard;
